import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // JWT user (email/password)
  const [gUser, setGUser] = useState(null); // Google user (session_token cookie)
  const [loading, setLoading] = useState(true);

  // Ask backend who we are. Try Google cookie first (no localStorage state needed),
  // then fall back to the legacy JWT token in localStorage.
  const checkAuth = useCallback(async () => {
    // If arriving from Google OAuth callback, defer to the hash handler.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/google/me", { withCredentials: true });
      setGUser(data);
    } catch { /* no google session, that's fine */ }

    const token = localStorage.getItem("looxs_token");
    if (token) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch { localStorage.removeItem("looxs_token"); }
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("looxs_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("looxs_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = useCallback(() => {
    const redirectUrl = window.location.origin + "/";
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Called by the root hash-handler once we're back from Emergent with #session_id=...
  const exchangeGoogleSession = useCallback(async (sessionId) => {
    const { data } = await api.post(
      "/auth/google/session",
      { session_id: sessionId },
      { withCredentials: true },
    );
    setGUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    // Clear both auth paths so the header state is truly logged-out.
    localStorage.removeItem("looxs_token");
    setUser(null);
    try { await api.post("/auth/google/logout", {}, { withCredentials: true }); } catch { /* noop */ }
    setGUser(null);
  }, []);

  const value = useMemo(() => {
    const currentUser = user || gUser;
    return {
      user: currentUser, loading,
      isGoogleUser: !!gUser && !user,
      login, register, logout,
      loginWithGoogle, exchangeGoogleSession,
    };
  }, [user, gUser, loading, login, register, logout, loginWithGoogle, exchangeGoogleSession]);

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
